import { toast } from "react-toastify";
import { ClientMapping, ClientMappingFormFields, ClientMappingTarget } from "models/client-mapping";

// API only: the page dispatches the slice actions after these resolve.
const BASE = `${process.env.REACT_APP_API_URL}/client-mappings`;

export interface ClientMappingValues {
    name: string
    ownerId: string | null
    clientIds: string[]
    targets: ClientMappingTarget[]
}

const send = async (url: string, method: string, body?: unknown) => {
    const res = await fetch(url, {
        credentials: 'include',
        method,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.message ?? "Request failed");
    }
    return res.json();
}

export const getClientMappings = async (): Promise<ClientMapping[] | null> => {
    try {
        return (await send(BASE, 'GET')).data as ClientMapping[];
    } catch (err: any) {
        toast.error(err.message ?? "Error getting clients");
        return null;
    }
}

export const getClientMappingFormFields = async (): Promise<ClientMappingFormFields | null> => {
    try {
        return (await send(`${BASE}/form-fields`, 'GET')).data as ClientMappingFormFields;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting form fields");
        return null;
    }
}

export const submitCreateClientMapping = async (vals: ClientMappingValues): Promise<ClientMapping | null> => {
    try {
        const data = (await send(BASE, 'POST', vals)).data as ClientMapping;
        toast.success("Client created.");
        return data;
    } catch (err: any) {
        toast.error(err.message ?? "Error creating client");
        return null;
    }
}

export const submitEditClientMapping = async (id: string, vals: ClientMappingValues): Promise<ClientMapping | null> => {
    try {
        const data = (await send(`${BASE}/${id}`, 'PATCH', vals)).data as ClientMapping;
        toast.success("Client updated.");
        return data;
    } catch (err: any) {
        toast.error(err.message ?? "Error updating client");
        return null;
    }
}

export const deleteClientMappingRequest = async (id: string): Promise<boolean> => {
    try {
        await send(`${BASE}/${id}`, 'DELETE');
        toast.success("Client deleted.");
        return true;
    } catch (err: any) {
        toast.error(err.message ?? "Error deleting client");
        return false;
    }
}
